import { throttle, omit } from 'lodash'
import colorUtils from 'storybook-utils/colors'
import Sentry from '@/services/sentry'
import ChatApi from '@/api/chat'
import Socket from '@/socket'
import Util from '@/util'
import eventCategories from '@/constant/eventCategories'
import QuizStatuses from '@/constant/QuizStatuses'
import * as localStorageExpiry from '@/utils/local.storage.expiry'

const SET_SENDING_MESSAGE = 'SET_SENDING_MESSAGE'
const SET_SUBSCRIBING_ROOM = 'SET_SUBSCRIBING_ROOM'
const SET_ACCESS_CODE = 'SET_ACCESS_CODE'
const ADD_MESSAGE = 'ADD_MESSAGE'
const SET_MESSAGES = 'SET_MESSAGES'
const TOGGLE_CHAT_BOX = 'TOGGLE_CHAT_BOX'
const TOGGLE_FOCUS_CHAT_INPUT = 'TOGGLE_FOCUS_CHAT_INPUT'
const SHOW_CHAT_NOTIFICATION = 'SHOW_CHAT_NOTIFICATION'
const MAX_MESSAGES_IN_CHAT = 200
const MESSAGE_EXPIRY = 30 * 24 * 60 * 60 * 1000

const Events = {
  NEW_MESSAGE: 'chat:new-message',
}

const getStorage = () => {
  return window.localStorage
}

const getStorageKey = (accessCode) => {
  return `chat-messages-${accessCode}`
}

export const getMessagesLocally = (accessCode) => {
  let messages = localStorageExpiry.getWithExpiry(getStorageKey(accessCode))
  if (!messages) {
    messages = '[]'
    localStorageExpiry.setWithExpiry(getStorageKey(accessCode), messages, MESSAGE_EXPIRY)
  }
  return JSON.parse(messages)
}

const saveMessagesLocally = ({ accessCode, messages, isThrottled }) => {
  // save messages every 10 seconds, because writing to the
  // localStorage is synchronous, and it would block the application
  // if there're lots of incoming messages
  if (isThrottled) {
    return throttle(() => {
      localStorageExpiry.setWithExpiry(getStorageKey(accessCode), JSON.stringify(messages), MESSAGE_EXPIRY)
    }, 10e3)
  }
  return () => {
    localStorageExpiry.setWithExpiry(getStorageKey(accessCode), JSON.stringify(messages), MESSAGE_EXPIRY)
  }
}

const deleteMessagesLocally = ({ uniqueAccessCode }) => {
  getStorage().removeItem(getStorageKey(uniqueAccessCode))
}

const convertMessage = (data) => {
  if (!data) return {}
  data.received = true
  return data
}

const getMessageIndex = (messages, msg) => {
  if (!messages || !msg) return -1
  return messages.findIndex((m) => m.timestamp === msg.timestamp)
}

const getChatOrigin = (state, isQuizSlide) => {
  if (isQuizSlide && state.slideActive.quizStatus === QuizStatuses.LOBBY_SCREEN) {
    return 'Lobby'
  }
  if (state.slideActive.type === 'qrcode') {
    return 'QR code'
  }
  if (state.slideActive.type === 'leaderboard') {
    return 'Leaderboard'
  }
  return 'Quiz'
}

const _state = {
  isSendingMessage: false,
  accessCode: null,
  newMessageHandler: null,
  isSubscribingChatRoom: false,
  messages: [],
  isOpenChatBox: false,
  isFocusingChatInput: false,
  hasChatNotification: false,
}

const getters = {
  isSendingMessage(state) {
    return state.isSendingMessage
  },
  isSubscribingChatRoom(state) {
    return state.isSubscribingChatRoom
  },
  chatMessages(state, __, ___, rootGetters) {
    const isShownEmojiAvatar = rootGetters['presentationSetting/isShownEmojiAvatar']
    return state.messages.map((message) => {
      return isShownEmojiAvatar ? message : omit(message, 'avatar')
    })
  },
  isOpenChatBox(state) {
    return state.isOpenChatBox
  },
  isFocusingChatInput(state) {
    return state.isFocusingChatInput
  },
  hasChatNotification(state) {
    return state.hasChatNotification
  },
}

const actions = {
  subscribeChatRoomAction: async ({ commit }, { presentation, newMessageHandler }) => {
    if (presentation && presentation.uniqueAccessCode) {
      commit(SET_SUBSCRIBING_ROOM, true)
      commit(SET_ACCESS_CODE, presentation.uniqueAccessCode)
      commit(SET_MESSAGES, getMessagesLocally(presentation.uniqueAccessCode))
      Socket.getSocket().on(Events.NEW_MESSAGE, (data) => {
        const messageObj = convertMessage(data)
        newMessageHandler(messageObj)
        commit(ADD_MESSAGE, messageObj)
      })
    }
  },

  unsubscribeChatRoomAction: async ({ commit }, { presentation }) => {
    if (presentation && presentation.uniqueAccessCode) {
      commit(SET_SUBSCRIBING_ROOM, false)
      Socket.getSocket().off(Events.NEW_MESSAGE)
    }
  },

  audienceChangeStatusAction: async ({ commit }, audience) => {
    const audienceColor = colorUtils.getAudienceColor(audience.audience)
    const messageObj = {
      type: 'change-online-status',
      userId: audience.audience,
      role: 'audience',
      name: audience.audienceName,
      avatar: audience.audienceEmoji,
      teamName: audience.audienceTeamName,
      message: audience.offline ? 'hasLeft' : 'hasJoined',
      timestamp: new Date().getTime(),
      color: audienceColor,
    }
    if (audience.audienceName) {
      commit(ADD_MESSAGE, messageObj)
    }
  },

  sendMessageAction: async ({ commit, rootState, rootGetters }, { payload, onSuccess, onFailure }) => {
    commit(SET_SENDING_MESSAGE, true)
    const chatOrigin = getChatOrigin(rootState, rootGetters['slideModule/isQuizSlide'](rootState.slideActive))

    try {
      // Add the new message to the chatbox rightaway (status = sending)
      commit(ADD_MESSAGE, payload)
      await ChatApi.sendMessage(payload)
      commit(SET_SENDING_MESSAGE, false)
      if (onSuccess) {
        onSuccess(payload.message)
      }

      Util.mixpanelTracking(
        eventCategories.PRESENTING_MODE,
        `Chat > ${chatOrigin} > Presenter sends a message`,
        payload.presentationId,
        payload.message,
      )
    } catch (error) {
      Sentry.captureException(error)
      commit(SET_SENDING_MESSAGE, false)
      commit(ADD_MESSAGE, { ...payload, received: false })
      Util.mixpanelTracking(
        eventCategories.PRESENTING_MODE,
        `Chat > ${chatOrigin} > Presenter sends a message`,
        `${payload.presentationId} > Chat > Presenter > ${error}`,
      )
      if (onFailure) {
        onFailure(error)
      }
    }
  },

  clearChatMessagesAction: async ({ commit }, { uniqueAccessCode }) => {
    commit(SET_MESSAGES, [])
    deleteMessagesLocally({ uniqueAccessCode })
  },

  toggleChatBoxAction: async ({ commit }, openState) => {
    if (openState) {
      commit(TOGGLE_CHAT_BOX, openState.isOpen)
    } else {
      commit(TOGGLE_CHAT_BOX)
    }
  },

  toggleFocusChatInputAction: async ({ commit }, focusState) => {
    if (focusState) {
      commit(TOGGLE_FOCUS_CHAT_INPUT, focusState.isFocus)
    } else {
      commit(TOGGLE_FOCUS_CHAT_INPUT)
    }
  },

  showChatNotificationAction: async ({ commit }, showState) => {
    commit(SHOW_CHAT_NOTIFICATION, showState.isShowing)
  },
}

const mutations = {
  [SET_SENDING_MESSAGE](state, isSendingMessage) {
    state.isSendingMessage = isSendingMessage
  },
  [SET_SUBSCRIBING_ROOM](state, isSubscribingChatRoom) {
    state.isSubscribingChatRoom = isSubscribingChatRoom
  },
  [SET_ACCESS_CODE](state, accessCode) {
    state.accessCode = accessCode
  },
  [ADD_MESSAGE](state, newMessage) {
    const index = getMessageIndex(state.messages, newMessage)
    if (index === -1) {
      state.messages = [
        ...state.messages,
        newMessage,
      ]

      if (state.messages.length > MAX_MESSAGES_IN_CHAT) {
        // Is it okay to mutate?
        state.messages.splice(0, 1)
      }

      const saveMessageThrottled = saveMessagesLocally({
        accessCode: state.accessCode,
        messages: state.messages,
        throttled: true,
      })
      saveMessageThrottled()
    } else {
      const oldMsgs = [...state.messages]
      oldMsgs[index] = newMessage
      state.messages = oldMsgs
      const saveMessage = saveMessagesLocally({
        accessCode: state.accessCode,
        messages: state.messages,
        throttled: false,
      })
      saveMessage()
    }
  },
  [SET_MESSAGES](state, messages) {
    state.messages = messages
  },
  [TOGGLE_CHAT_BOX](state, openState) {
    if (typeof openState === 'boolean') {
      state.isOpenChatBox = openState
    } else {
      state.isOpenChatBox = !state.isOpenChatBox
    }
  },
  [TOGGLE_FOCUS_CHAT_INPUT](state, focusState) {
    if (typeof focusState === 'boolean') {
      state.isFocusingChatInput = focusState
    } else {
      state.isFocusingChatInput = !state.isFocusingChatInput
    }
  },
  [SHOW_CHAT_NOTIFICATION](state, showState) {
    state.hasChatNotification = showState
  },
}

export default {
  namespaced: true,
  state: _state,
  getters,
  actions,
  mutations,
}
